import React, { useState } from 'react';
import { Box, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import imgPlaceholder from '../../assets/images/imgPlaceholder.png';
import { DEFAULT_SHOPPING_LIST_SERVINGS } from '../../features/end_user_shopping/services/shopping_lists';
import QuantityInput from '../common/quantity_input';
import MealDetailModal from '../meal/meal_detail_modal';
import {
  useListProductsBoardQuery,
  useUpdateShoppingListMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { userLocale } from '../../services/users';
import {
  shoppingListHasFinishedChanges,
  shoppingListIsPendingChanges,
} from '../../features/end_user_shopping/end_user_shopping_slice';
import { compileShoppingListOperation } from '../../operations/meal_operations';
import { setMealQuantity } from '../../services/meals';
import {
  useGetShoppingListByIDAndParentQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';

const ShoppingListMealsList = ({
  shoppingListId,
  groupedMeals,
  changesPending,
}) => {
  const dispatch = useDispatch();

  const { data: userProfile } = useGetUserProfileQuery();

  const { data: productsBoard } = useListProductsBoardQuery();

  const { data: shoppingList } = useGetShoppingListByIDAndParentQuery(
    { id: shoppingListId, parentID: productsBoard?.id },
    {
      skip: !shoppingListId || !productsBoard?.id,
    },
  );

  const [updateShoppingList] = useUpdateShoppingListMutation();

  const locale = userLocale(userProfile);

  const [mealDetailVisibleFor, setMealDetailVisibleFor] = useState(null);

  const onChangeQuantity = async (group, quantityStr) => {
    const quantity = parseInt(quantityStr, 10);
    if (quantity > 0) {
      dispatch(shoppingListIsPendingChanges(shoppingListId));
      try {
        const meals = setMealQuantity(
          shoppingList.meals,
          group.shoppingListMealIDs[0],
          quantity,
        );
        // const locallyUpdatedList = {
        //   ...shoppingList,
        //   meals,
        // };
        // TODO locally update the meals first
        const newShoppingList = await compileShoppingListOperation(
          meals,
          DEFAULT_SHOPPING_LIST_SERVINGS,
          null,
          locale,
        );
        const updatedList = {
          ...shoppingList,
          combinedItems: newShoppingList.items,
          meals: newShoppingList.meals,
        };
        await updateShoppingList(updatedList);
      } finally {
        dispatch(shoppingListHasFinishedChanges(shoppingListId));
      }
    }
    console.log(quantity);
  };

  /**
   * This action rebuilds the shopping list from the meals, discarding
   * any items added by the user to the shopping list directly.
   */
  const onDelete = async (/* group */) => {
    if (!changesPending) {
      try {
        dispatch(shoppingListIsPendingChanges(shoppingListId));
        // dispatch({
        //   type: 'MEALS_DELETED_FROM_SHOPPING_LIST_TEMP',
        //   shoppingListId,
        //   shoppingListMealIDs: group.shoppingListMealIDs,
        // });
        const newShoppingList = await compileShoppingListOperation(
          shoppingList.meals,
          DEFAULT_SHOPPING_LIST_SERVINGS,
          null,
          locale,
        );
        const updatedShoppingList = {
          ...shoppingList,
          items: newShoppingList.items,
          meals: newShoppingList.meals,
        };
        await updateShoppingList(updatedShoppingList);
      } finally {
        dispatch(shoppingListHasFinishedChanges(shoppingListId));
      }
    }
  };

  const onShowMealDetail = (meal) => {
    setMealDetailVisibleFor(meal);
  };

  const onHideMealDetail = () => {
    setMealDetailVisibleFor(null);
  };

  console.log(JSON.stringify({ mealDetailVisibleFor }));

  return (
    <>
      <div
        style={{
          maxHeight: '200px',
          overflowY: 'auto',
        }}>
        <Box sx={{ display: 'flex' }}>
          <div style={{ width: 72 }}>&nbsp;</div>
          <div style={{ flexGrow: 1 }}>&nbsp;</div>
          <div style={{ width: 75, fontSize: '12px', fontWeight: 'bold' }}>
            Count
          </div>
        </Box>
        {groupedMeals.map((group) => {
          if (
            !group ||
            !group.meal ||
            !group.meal.recipes ||
            !group.meal.recipes[0]
          ) {
            // Deleted meal or meal not migrated
            return '';
          }
          const recipe = group.meal.recipes[0];
          return (
            <Box key={group.meal.mealID} sx={{ display: 'flex' }}>
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
              <div
                onClick={(ev) => {
                  ev.stopPropagation();
                  onShowMealDetail(group.meal);
                }}
                style={{
                  backgroundImage: `url("${
                    recipe?.imageUrl || imgPlaceholder
                  }")`,
                  width: 72,
                  height: 72,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  flexShrink: 0,
                  margin: '2px',
                }}>
                &nbsp;
              </div>
              <div style={{ flexGrow: 1, marginLeft: '10px' }}>
                {recipe.title}
              </div>
              <div style={{ flexShrink: 0 }}>
                <QuantityInput
                  initialValue={group.shoppingListMealIDs.length}
                  width={35}
                  disabled={changesPending}
                  onChange={(newValue) => onChangeQuantity(group, newValue)}
                />
                <IconButton
                  aria-label="delete"
                  size="small"
                  style={{ marginRight: '5px', flexShrink: 0 }}
                  disabled={changesPending}
                  onClick={(ev) => {
                    onDelete(group);
                    ev.stopPropagation();
                  }}>
                  <DeleteIcon />
                </IconButton>
              </div>
            </Box>
          );
        })}
      </div>
      {mealDetailVisibleFor && (
        <MealDetailModal
          fullMeal={mealDetailVisibleFor}
          isLoading={false}
          menu={null}
          isReadOnly
          savingInProgress={false}
          canScaleIngredients={false}
          scaleToServings={mealDetailVisibleFor.recipes[0].servings}
          onDialogClose={onHideMealDetail}
        />
      )}
    </>
  );
};

ShoppingListMealsList.propTypes = {
  shoppingListId: PropTypes.string.isRequired,
  groupedMeals: PropTypes.arrayOf(
    PropTypes.shape({
      meal: PropTypes.shape({ mealID: PropTypes.string.isRequired }).isRequired,
      shoppingListMealIDs: PropTypes.arrayOf(PropTypes.string),
    }),
  ).isRequired,
  changesPending: PropTypes.bool,
};

ShoppingListMealsList.defaultProps = {
  changesPending: false,
};

export default ShoppingListMealsList;
